/**
 * AI Assistant — `agenda` block (G2, COPILOT_API_CONTRACT.md "G2 blocks").
 *
 *   { kind: 'agenda', title, items: [ { at, type, title, subtitle, job_id, application_id, status } ] }
 *   type: interview | test | offer_expiry | joining
 *
 * Items are grouped by day ("Today", "Tomorrow", else "EEE, d MMM"); every row
 * is one button: with an `application_id` it opens the candidate (cross-job
 * pending intent lives in CopilotContext), otherwise it opens the job.
 * Compact house style: 13 px title, 12 px muted subtitle, tabular time.
 * Colour is never the only signal — each type also has its own icon.
 */
import React from 'react';
import { format, isToday, isTomorrow, startOfDay } from 'date-fns';
import { AlertTriangle, ClipboardCheck, UserCheck, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCopilot } from './CopilotContext';
import { EmptyBlock, StatusPill } from './CopilotBlocks';

export const AGENDA_TYPES = Object.freeze({
  interview: { icon: Video, label: 'Interview' },
  test: { icon: ClipboardCheck, label: 'Test' },
  offer_expiry: { icon: AlertTriangle, label: 'Offer expires' },
  joining: { icon: UserCheck, label: 'Joining' },
});

const AGENDA_OK = new Set(['confirmed', 'completed', 'accepted', 'joined', 'scheduled']);
const AGENDA_BAD = new Set(['cancelled', 'canceled', 'expired', 'no_show', 'declined', 'overdue', 'missed']);

/** Agenda status band: 'ok' | 'bad' | 'neutral'. */
export function agendaStatusBand(status) {
  const key = String(status || '').toLowerCase().trim();
  if (AGENDA_OK.has(key)) return 'ok';
  if (AGENDA_BAD.has(key)) return 'bad';
  return 'neutral';
}

function parseAt(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Today" / "Tomorrow" / "Mon, 27 Oct" for a day; `null` date → "No date". */
export function agendaDayLabel(date, now = new Date()) {
  if (!date) return 'No date';
  if (isToday(date) && isToday(now)) return 'Today';
  if (isTomorrow(date) && isToday(now)) return 'Tomorrow';
  return format(date, 'EEE, d MMM');
}

/**
 * Sort items by time and group them by calendar day.
 * Returns [{ key, label, items: [{ item, date, index }] }]; undated items last.
 */
export function groupAgendaItems(items, now = new Date()) {
  const dated = [];
  const undated = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const date = parseAt(item.at);
    if (date) dated.push({ item, date });
    else undated.push({ item, date: null });
  });
  dated.sort((a, b) => a.date - b.date);
  const groups = [];
  const byKey = new Map();
  let index = 0;
  [...dated, ...undated].forEach((entry) => {
    const key = entry.date ? String(startOfDay(entry.date).getTime()) : 'undated';
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: agendaDayLabel(entry.date, now), items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.items.push({ ...entry, index });
    index += 1;
  });
  return groups;
}

export function AgendaBlock({ block }) {
  const copilot = useCopilot();
  const groups = groupAgendaItems(block?.items);
  if (!groups.length) return <EmptyBlock block={{ text: 'Nothing scheduled' }} />;

  const openItem = (item) => {
    const appId = item.application_id ?? item.applicationId ?? null;
    const jobId = item.job_id ?? item.jobId ?? null;
    if (appId !== null && appId !== undefined) return copilot.viewProfile(appId, jobId ?? undefined);
    if (jobId !== null && jobId !== undefined) return copilot.navigateTo({ target: 'job_applications', job_id: jobId, label: item.title });
    return false;
  };

  return (
    <div className="space-y-1.5" data-testid="copilot-agenda">
      {block?.title && <div className="text-xs font-semibold text-foreground/90">{block.title}</div>}
      {groups.map((group) => (
        <section key={group.key} aria-label={group.label} data-testid="copilot-agenda-day">
          <div className="cp-label mb-1 px-0.5" data-testid="copilot-agenda-day-label">
            {group.label}
          </div>
          <ul className="cp-stagger space-y-1">
            {group.items.map(({ item, date, index }) => {
              const type = String(item.type || '').toLowerCase();
              const meta = AGENDA_TYPES[type] || AGENDA_TYPES.interview;
              const Icon = meta.icon;
              const title = item.title || meta.label;
              const clickable =
                (item.application_id ?? item.applicationId ?? null) !== null || (item.job_id ?? item.jobId ?? null) !== null;
              return (
                <li key={`${item.at || 'x'}-${index}`} style={{ '--i': index }}>
                  <button
                    type="button"
                    className="cp-card cp-card-hover cp-focus flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left disabled:cursor-default"
                    onClick={() => openItem(item)}
                    disabled={!clickable}
                    aria-label={`${title}${date ? `, ${format(date, 'h:mm a')}` : ''}`}
                    data-type={type || undefined}
                    data-testid="copilot-agenda-row"
                  >
                    <span className="cp-num cp-muted w-[56px] shrink-0 text-[11px] leading-4" data-testid="copilot-agenda-time">
                      {date ? format(date, 'h:mm a') : '—'}
                    </span>
                    <span
                      className="cp-agenda-icon inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
                      data-type={AGENDA_TYPES[type] ? type : 'interview'}
                      title={meta.label}
                      aria-hidden="true"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium leading-4 text-foreground" title={title}>
                        {title}
                      </span>
                      {item.subtitle && (
                        <span className="cp-muted block truncate text-[12px] leading-4" title={item.subtitle}>
                          {item.subtitle}
                        </span>
                      )}
                    </span>
                    {item.status && <StatusPill status={item.status} band={agendaStatusBand(item.status)} className={cn('max-w-[35%] truncate')} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default AgendaBlock;
